import React, {createRef, useState, useContext} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import __ from "lodash";
import moment from "moment";
import MaterialTable from "@material-table/core";
import {defaultComponentsScroll, MaterialTableBody, MaterialTableBodyRow} from "../../../controls/Material-Table-Components"
import {TablePagination, Button, Chip, FormControl, FormGroup, TextField, useTheme} from "@material-ui/core";
import SystemContext from "../../../SystemContext";
import useDimensions from '../../../blocks/useDimensions';
import {LightTooltip} from "../../../controls";
import { ValidatorColumn, ColumnWidth } from "../../../blocks/ColumnHelper";
import Validator from "../validators/UldTrackingValidator";
import { useUldTracker } from "../../../services/uld";
import AirportNameTooltip from "../../Airports/components/AirportNameTooltip";
import ViewSourceMessageDialog from "./ViewSourceMessageDialog";


const UldTrackingList = (_props) => {
  const {isAuthenticated} = useAuth0();
  const SystemData = useContext(SystemContext);
  const theme = useTheme();
  const eventBus = SystemData.eventBus;
  const validatorColumn = ValidatorColumn(Validator, true);

  const props = {
    ..._props
  }
  const [state, setState] = useState({
    canEdit: props.canEdit,
    activeRow: null,
    uld: {}
  });
  const classes = props.classes;
  if (eventBus) {
    eventBus.bind('UldTrackingList.rowSelected', (uld)=>{
      setState({...state, uld: uld});
    });
  }

  const uld = state.uld;

  const tableRef = createRef({state: {lastEditingRow: false}});

  const queryClient = useQueryClient();
  const extraKeys = [uld.uldNumber];

  const uldTrackingRecords = useUldTracker({tableRef, queryClient, extraKeys});

  const getColumns = (props) => {
    const baseColumn = {
      cellStyle: { fontSize:14, padding: "8px 2px" },
      headerStyle: { padding: "8px 0px" },
      filterCellStyle: { padding: "8px 2px" },
      editable: props.canEdit ? "always" : "never",
      emptyValue: "",
      initialEditValue: "",
      sorting: false,
    };

    const defaultEditProps = {
      style:{fontSize: "0.875rem"}
    };

    return [
      {...validatorColumn},
      /*
        Can use ...ColumnWidth(70) to generate a fixed width column
      */

      {
        ...baseColumn,
        ...ColumnWidth(120, undefined,undefined,undefined, {width: 76},undefined,false),
        title: "Event Time",
        field: "eventTime",
        sorting: false,
        editable: "never",
        render: (rowData) => (<span>{moment(rowData.eventTime).format("DD/MM/YYYY HH:MM")}</span>)
      },
      {
        ...baseColumn,
        ...ColumnWidth(70, undefined,undefined,undefined, {width: 76},undefined,false),
        title: "Source",
        field: "eventType",
        sorting: false,
        editable: "never",
      },
      {
        ...baseColumn,
        ...ColumnWidth(60, undefined,undefined,undefined, {width: 76},undefined,false),
        title: "Loaded",
        field: "airportOfLoading",
        sorting: false,
        editable: "never",
        render: (rowData) => (
          <LightTooltip title={<AirportNameTooltip airportCode={rowData.airportOfLoading} />}>
            <div>
              <span> {rowData.airportOfLoading} </span>
            </div>
          </LightTooltip>
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(80, undefined,undefined,undefined, {width: 76},undefined,false),
        title: "Direction",
        field: "eventDirection",
        sorting: false,
        editable: "never",
        render: (rowData) => (<span> {rowData.eventDirection ? rowData.eventDirection === "OUT" ? "Outbound" : "Inbound" : ["CPM", "UWS"].includes(rowData.eventType) ? "Outbound" : "" } </span>),
      },
      {
        ...baseColumn,
        ...ColumnWidth(70, undefined,undefined,undefined, {width: 76},undefined,false),
        title: "Unloaded",
        field: "airportOfUnloading",
        initialEditValue: "",
        sorting: false,
        editable: "never",
        render: (rowData) => (
          <LightTooltip title={<AirportNameTooltip airportCode={rowData.airportOfUnloading} />}>
            <div>
              <span> {rowData.airportOfUnloading} </span>
            </div>
          </LightTooltip>
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(60, undefined,undefined,undefined, {width: 76},undefined,false),
        title: "Flight",
        field: "eventFlightNumber",
        sorting: false,
        editable: "never",
        render: (rowData) => (<span> {rowData.eventFlightNumber && rowData.eventFlightNumber !== "undef" ? rowData.eventFlightNumber : ""} </span>),
      },
      {
        ...baseColumn,
        ...ColumnWidth(70, undefined,undefined,undefined, undefined,undefined,false),
        title: "Position",
        field: "uldLoadingPosition",
        initialEditValue: "",
      },
      {
        ...baseColumn,
        ...ColumnWidth(70, undefined,undefined,undefined, {width: 76},undefined,false),
        title: "Contour",
        field: "uldContourCode",
        initialEditValue: "",
      },
      {
        ...baseColumn,
        ...ColumnWidth(70, undefined,undefined,undefined, {width: 76},undefined,false),
        title: "Category",
        field: "loadCategory",
        initialEditValue: "",
        render: (rowData) => {
          const categoryCodes = [
            {code: "C", value: "Cargo"},
            {code: "M", value: "Mail"},
            {code: "E", value: "Equipment"},
            {code: "X", value: "Empty"},
            {code: "U", value: "Unservicable"},
          ];
          const category = __.find(categoryCodes, {code: rowData.loadCategory});
          return <span> {category ? category.value : ""} </span>
        },
      },
      {
        ...baseColumn,
        ...ColumnWidth(60, undefined,undefined,undefined, undefined,undefined,false),
        title: "Pieces",
        field: "loadedPieces",
        render: (rowData) => (<span> {rowData.loadedPieces ? rowData.loadedPieces : rowData.eventPieces ? rowData.eventPieces : ""} </span>),
      },
      {
        ...baseColumn,
        ...ColumnWidth(120, undefined,undefined,undefined, undefined,undefined,false),
        title: "Weight",
        field: "loadedWeight",
        render: (rowData) => (<span> {rowData.loadedWeight ? `${rowData.loadedWeight}${rowData.weightUom} ${rowData.weightStatus === "A" ? "Actual" : rowData.weightStatus === "P" ? "Provisional" : ""}` : rowData.eventWeight ? `${rowData.eventWeight}${rowData.eventWeightUom} ${rowData.weightStatus === "A" ? "Actual" : rowData.weightStatus === "P" ? "Provisional" : ""}` : ""} </span>),
      },
      {
        ...baseColumn,
        ...ColumnWidth(80, undefined,undefined,undefined, undefined,undefined,false),
        title: "Volume",
        field: "loadedVolume",
        render: (rowData) => (<span> {rowData.loadedVolume ? `${rowData.loadedVolume}${rowData.volumeUom}` : rowData.eventVolume ? `${rowData.eventVolume}${rowData.eventVolumeUom}` : ""} </span>),
      },
      {
        ...baseColumn,
        ...ColumnWidth(300, undefined,undefined,undefined, undefined,undefined,false),
        title: "Special Handling",
        field: "shcs",
        render: (rowData) => (
          <div className={classes.chips}>
            {(rowData.shcs||[]).map((value) => (
              <Chip key={value} label={value} className={classes.chip} size={"small"}/>
            ))}
          </div>
        )
      },
      {
        ...baseColumn,
        title: "Source Message",
        field: "messageContent",
        render: (rowData) => (
          <Button onClick={()=>{eventBus.trigger("ViewSourceMessageDialog.open", rowData);}}>
            <div style={{width:200, maxWidth:200, textOverflow: "ellipsis", whiteSpace:"nowrap", overflow: "hidden"}}>
              {rowData.messageContent}
            </div>
          </Button>),
      },
    ];
  };

  // eslint-disable-next-line no-unused-vars
  const [tableContainerRef, { width: tableWidth, height: tableHeight}] = useDimensions({});

  const theader = tableContainerRef.current?.querySelector('thead');
  const thead = theader ? theader.clientHeight:36;
  const bodyHeight = (tableHeight - thead) - 14 ;

  const bodyRef = createRef();

  return (isAuthenticated &&
    <div style={{width: "100%", marginLeft: 20, marginRight: 20}}>
      <div ref={tableContainerRef} className={state.canEdit?classes.tariffLineListEdit:classes.tariffLineList} style={{width: "100%", minWidth: "100%", height: `calc(100vh - ${props.height + 82}px)`}}>
      {!state.uld || Object.keys(state.uld).length === 0?(
        <div style={{position: "relative", width: "100%", top: "50%", height: "30px", textAlign: "center"}}>
          <Button color={"primary"}>Select ULD to view its tracking information</Button>
        </div>
      ):(
        <div>
          <MaterialTable
            tableRef={tableRef}
            style={{
              border: "none",
              boxShadow: "none",
              borderRadius: 0,
            }}
            options={{
              actionsColumnIndex: -1,
              cellStyle: {
                padding: "8px 2px",
              },
              headerRowStyle: {
                display: "table",
                width: "100%",
              },
              headerStyle: {
                backgroundColor: theme.palette.tableHeader.backgroundColor,
                color: theme.palette.tableHeader.textColor,
                padding: "2px",
              },
              draggable: false,
              padding: "dense",
              toolbarButtonAlignment: "right",
              pageSize: 10,
              detailPanelType: "single",
              paging: false,
            }}
            components={{
              ...defaultComponentsScroll,
              Body: (props) => (
                <MaterialTableBody
                  {...props}
                  rowHeight={40}
                  overscanRowCount={10}
                  addVirtualScrolling={true}
                  bodyHeight={bodyHeight}
                  headerHeight={thead}
                  filterRowHeight={56}
                />
              ),
              Pagination: (props) => (
                <TablePagination
                  {...props}
                  rowsPerPageOptions={[5, 10, 15]}
                />
              ),
            }}
            title={`Tracking Information for ${uld.uldNumber}`}
            columns={getColumns({canEdit: state.canEdit})}
            data={uldTrackingRecords.data}
            editable={{
              isEditable: false,
              isDeletable: false,
              onRowAdd: undefined,
              onRowUpdate: undefined
            }}
          />
          <ViewSourceMessageDialog
            systemData={SystemData}
            extraKeys={extraKeys}
            classes={classes}
            canEdit={state.canEdit}
          />
        </div>
      )}
      </div>
    </div>
  );

}

export default UldTrackingList;
